import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { UserService, UserServiceContext, useCurrentAvatar } from '@entities/User'
import { useLocation } from 'react-router-dom'
import { publicRoutes } from '@/shared/constants/publicRoutes'
import { CustomLoader } from '@/shared/animations/loaders/CustomLoader'
import { useAuthMode } from '@app/providers/authModeProvider'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { userQueryKeys } from '@entities/User/config/query-keys'

const getAccessTokenSubject = (accessToken: string): string => {
	try {
		const [, payload] = accessToken.split('.')
		let normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
		while (normalizedPayload.length % 4) {
			normalizedPayload += '='
		}
		const decodedPayload = JSON.parse(atob(normalizedPayload))

		return decodedPayload.sub || decodedPayload.id || 'current'
	} catch {
		return 'current'
	}
}

const TokenUserServiceProvider = ({
	children,
	accessToken,
	ensureFreshSession,
}: {
	children: ReactNode
	accessToken: string
	ensureFreshSession: () => Promise<string | null>
}) => {
	const queryClient = useQueryClient()
	const ensureFreshSessionRef = useRef(ensureFreshSession)
	ensureFreshSessionRef.current = ensureFreshSession
	const store = useMemo(() => {
		const service = new UserService(null, () => ensureFreshSessionRef.current())
		service.setToken(accessToken)
		return service
	}, [accessToken])
	const userId = useMemo(() => getAccessTokenSubject(accessToken), [accessToken])
	const profileQuery = useQuery({
		queryKey: userQueryKeys.currentProfile(userId),
		queryFn: () => store.getUserProfileData(),
		enabled: Boolean(accessToken),
		staleTime: Infinity,
		cacheTime: Infinity,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		retry: false,
	})
	const currentAvatarQuery = useCurrentAvatar(Boolean(accessToken))

	useEffect(() => {
		store.setProfileDataUpdatedCallback((data) => {
			queryClient.setQueryData(userQueryKeys.currentProfile(userId), data)
		})

		return () => store.setProfileDataUpdatedCallback(null)
	}, [queryClient, store, userId])

	useEffect(() => {
		if (profileQuery.data) {
			store.hydrateProfileData(profileQuery.data)
		}
	}, [profileQuery.data, store])

	useEffect(() => {
		if (currentAvatarQuery.data) {
			store.setAvatar(currentAvatarQuery.data)
		}
	}, [currentAvatarQuery.data, store])

	return <UserServiceContext.Provider value={store}>{children}</UserServiceContext.Provider>
}

export const UserServiceContextProvider = ({ children }: { children: ReactNode }) => {
	const location = useLocation()
	const isPublicRoute = publicRoutes.includes(location.pathname)
	const auth = useAuthMode()

	if (isPublicRoute) {
		return <UserServiceContext.Provider value={null}>{children}</UserServiceContext.Provider>
	}

	if (auth.mode === 'token' && auth.accessToken) {
		return (
			<TokenUserServiceProvider
				accessToken={auth.accessToken}
				ensureFreshSession={auth.ensureFreshSession}>
				{children}
			</TokenUserServiceProvider>
		)
	}

	if (auth.mode === 'keycloak' || auth.mode === 'public') {
		return <CustomLoader />
	}

	return <CustomLoader />
}
